import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Code, Shield, Database, Zap, CheckCircle, ArrowRight, Server, Globe } from 'lucide-react';
import { motion } from 'framer-motion';

const Documentation = () => {
  return (
    <Layout>
      <SEOHead 
        title="NoQ5.ai Docs: OAuth2 Setup, Salesforce & HubSpot API Integration Guide"
        description="Technical documentation for NoQ5.ai: OAuth2 auth, native Salesforce & HubSpot API integration, GDPR compliance, multi-tenant security."
        canonicalUrl="https://noq5.ai/docs"
        keywords="NoQ5.ai documentation, Salesforce API integration, HubSpot API, OAuth2 setup, GDPR compliant API, CRM API documentation, Gong alternative, Clari alternative"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "TechArticle",
          "headline": "NoQ5.ai Technical Documentation",
          "description": "Complete technical documentation for integrating NoQ5.ai with Salesforce and HubSpot",
          "author": {
            "@type": "Organization",
            "name": "NoQ5.ai"
          }
        }]}
      />
      <div className="container px-4 md:px-6 py-12 max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-12"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-block rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary mb-4"
            >
              Developer Resources
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Technical Documentation
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Complete technical guide for integrating NoQ5.ai with Salesforce and HubSpot - OAuth2, API specs, multi-tenant architecture, and security
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { href: '#oauth', icon: Shield, title: 'OAuth2 Setup', desc: 'Authentication and security' },
              { href: '#api', icon: Code, title: 'API Integration', desc: 'Salesforce & HubSpot APIs' },
              { href: '#architecture', icon: Database, title: 'Architecture', desc: 'Multi-tenant & GDPR' },
            ].map((item, i) => (
              <motion.a
                key={i}
                href={item.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i }}
                className="p-6 bg-card rounded-2xl border border-border hover:border-primary/50 hover:shadow-lg transition-all group"
              >
                <item.icon className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-2 text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.a>
            ))}
          </div>

          {/* OAuth2 Authentication */}
          <section id="oauth" className="scroll-mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/5 via-card to-primary/5 rounded-2xl p-8 border border-primary/20"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">OAuth2 Authentication</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">Salesforce OAuth2 Flow</h3>
                  <div className="bg-card rounded-xl p-6 space-y-4 border border-border">
                    {[
                      { label: 'Authorization Endpoint', code: 'https://login.salesforce.com/services/oauth2/authorize' },
                      { label: 'Token Endpoint', code: 'https://login.salesforce.com/services/oauth2/token' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <strong className="text-foreground">{item.label}:</strong>
                          <code className="block mt-1 p-3 bg-muted rounded-lg text-sm font-mono text-foreground">
                            {item.code}
                          </code>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <strong className="text-foreground">Required Scopes:</strong>
                        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">api</code> - Access to Salesforce APIs</li>
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">refresh_token</code> - Offline access</li>
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">full</code> - Full access to all data (optional)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">HubSpot OAuth2 Flow</h3>
                  <div className="bg-card rounded-xl p-6 space-y-4 border border-border">
                    {[
                      { label: 'Authorization Endpoint', code: 'https://app.hubspot.com/oauth/authorize' },
                      { label: 'Token Endpoint', code: 'https://api.hubapi.com/oauth/v1/token' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <strong className="text-foreground">{item.label}:</strong>
                          <code className="block mt-1 p-3 bg-muted rounded-lg text-sm font-mono text-foreground">
                            {item.code}
                          </code>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <strong className="text-foreground">Required Scopes:</strong>
                        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">crm.objects.contacts.write</code></li>
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">crm.objects.companies.write</code></li>
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">crm.objects.deals.write</code></li>
                          <li>• <code className="bg-muted px-2 py-1 rounded text-foreground">crm.objects.custom.write</code></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-600" />
                    Multi-Tenant Token Isolation
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Each organization's OAuth tokens are encrypted at rest using AES-256 encryption and stored in isolated database partitions. Token refresh happens automatically with zero downtime. No token can access data from another organization.
                  </p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* API Integration */}
          <section id="api" className="scroll-mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/5 via-card to-primary/5 rounded-2xl p-8 border border-primary/20"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Code className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">API Integration Details</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">Salesforce REST API Integration</h3>
                  <div className="bg-card rounded-xl p-6 space-y-4 border border-border">
                    <div>
                      <strong className="block mb-2 text-foreground">Supported Operations:</strong>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        {[
                          { op: 'CREATE', desc: 'Insert new records (Leads, Contacts, Opportunities, Custom Objects)' },
                          { op: 'UPDATE', desc: 'Modify existing records via PATCH or PUT' },
                          { op: 'UPSERT', desc: 'Create or update based on external ID' },
                          { op: 'QUERY', desc: 'SOQL queries to retrieve context' },
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span><strong className="text-foreground">{item.op}:</strong> {item.desc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <strong className="block mb-2 text-foreground">Custom Object Mapping:</strong>
                      <p className="text-sm text-muted-foreground mb-3">
                        NoQ5.ai dynamically discovers your Salesforce schema using the Describe API and maps voice input to ANY standard or custom object/field.
                      </p>
                      <pre className="p-4 bg-muted rounded-lg text-xs overflow-x-auto font-mono text-foreground">
{`// Example: Voice input automatically mapped to custom object
Voice: "Create a new project called Alpha with budget 50k"

API Call:
POST /services/data/v59.0/sobjects/Custom_Project__c
{
  "Name": "Alpha",
  "Budget__c": 50000,
  "Status__c": "Planning"
}`}
                      </pre>
                    </div>

                    <div>
                      <strong className="block mb-2 text-foreground">Supported Standard Objects:</strong>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        {['Lead', 'Contact', 'Account', 'Opportunity', 'Task', 'Event', 'Case', 'Campaign'].map((obj) => (
                          <span key={obj} className="text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full text-center">
                            {obj}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">+ ALL custom objects (__c suffix)</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">HubSpot API Integration</h3>
                  <div className="bg-card rounded-xl p-6 space-y-4 border border-border">
                    <div>
                      <strong className="block mb-2 text-foreground">Supported HubSpot Objects:</strong>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        {[
                          { obj: 'Contacts', desc: 'Create and update contact records' },
                          { obj: 'Companies', desc: 'Manage company records' },
                          { obj: 'Deals', desc: 'Create and update deal pipeline' },
                          { obj: 'Tickets', desc: 'Service ticket management' },
                          { obj: 'Custom Objects', desc: 'Any custom CRM object' },
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span><strong className="text-foreground">{item.obj}:</strong> {item.desc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <strong className="block mb-2 text-foreground">API Endpoint Example:</strong>
                      <pre className="p-4 bg-muted rounded-lg text-xs overflow-x-auto font-mono text-foreground">
{`POST https://api.hubapi.com/crm/v3/objects/contacts
Authorization: Bearer {access_token}
Content-Type: application/json

{
  "properties": {
    "email": "john@example.com",
    "firstname": "John",
    "lastname": "Doe",
    "phone": "+1234567890"
  }
}`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Architecture */}
          <section id="architecture" className="scroll-mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/5 via-card to-primary/5 rounded-2xl p-8 border border-primary/20"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Multi-Tenant Architecture</h2>
              </div>

              <div className="space-y-6">
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Data Isolation & Security</h3>
                  <div className="space-y-4">
                    {[
                      { title: 'Tenant Isolation', desc: "Each organization's data is logically isolated using tenant_id partitioning. No cross-tenant queries are possible at the database level." },
                      { title: 'Token Encryption', desc: 'All OAuth tokens encrypted with AES-256-GCM before storage. Decryption keys rotated every 90 days.' },
                      { title: 'GDPR Compliance', desc: 'EU data residency (Ireland), built-in data anonymization, right to deletion, data portability via export API.' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <strong className="text-foreground">{item.title}:</strong>
                          <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Infrastructure</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { icon: Globe, label: 'Hosting', value: 'AWS EU-West-1 (Ireland)' },
                      { icon: Database, label: 'Database', value: 'PostgreSQL with row-level security' },
                      { icon: Zap, label: 'Voice Processing', value: 'OpenAI Whisper API (EU endpoints)' },
                      { icon: Server, label: 'AI Processing', value: 'GPT-4 with custom fine-tuning' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <item.icon className="w-5 h-5 text-primary" />
                        <div>
                          <strong className="text-sm text-foreground">{item.label}:</strong>
                          <p className="text-sm text-muted-foreground">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </section>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-primary rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">Ready to integrate?</h3>
            <p className="text-lg mb-8 text-primary-foreground/80 max-w-2xl mx-auto">
              Start your free trial and connect NoQ5.ai to your CRM in under 5 minutes.
            </p>
            <Button 
              size="lg"
              className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-xl"
              asChild
            >
              <a href="https://app.noq5.ai/trial" target="_blank" rel="noopener noreferrer">
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Documentation;